import { useContext, useEffect, useState } from 'react';
import {
  Button,
  FormGroup,
  Input,
  Label,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { featureCollection } from '@turf/turf';
import { useParams } from 'react-router';
import { FeatureCollection } from 'geojson';
import { noop } from 'lodash';
import Modal from '../../basics/Modal';
import { asQuantityWithUnit } from '../../../utils/formatters';
import { captureEvent } from '../../../utils/analytics';
import { MapContext } from '../../../contexts/MapContext';
import { fillLayerIds } from '../../../utils/layers';

const ReplaceFeaturesModal = ({
  onCompleted = noop,
  pendingFeatures,
  setPendingFeatures,
}: {
  onCompleted?: () => void;
  pendingFeatures: FeatureCollection;
  setPendingFeatures: (features: FeatureCollection) => void;
}) => {
  const { farmId } = useParams();
  const {
    // @ts-expect-error MapContext not migrated to TS
    mapState: { activeLayerId, activeLayer },
    // @ts-expect-error MapContext not migrated to TS
    layers: { bulkCreateLayerFeatures },
  } = useContext(MapContext);

  const [isOpen, setIsOpen] = useState(false);
  const [shouldReplaceFeatures, setShouldReplaceFeatures] = useState(false);

  useEffect(() => {
    if (!pendingFeatures?.features?.length) return;
    const { dataWithIds } = fillLayerIds(pendingFeatures, activeLayerId);

    if (activeLayer.data?.features.length > 0) {
      // If there are existing features, show the add/replace features modal
      setIsOpen(true);
    } else {
      captureEvent('layer-data-uploaded', {
        layerId: activeLayer.layerType,
        type: 'no existing features',
      });
      bulkCreateLayerFeatures({
        farmId,
        layerId: activeLayerId,
        fullLayerData: dataWithIds,
        newLayerFeatures: dataWithIds.features,
      });
      setPendingFeatures(featureCollection([]));
      onCompleted();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFeatures]);

  const toggle = () => {
    setIsOpen(false);
    setPendingFeatures(featureCollection([]));
    setShouldReplaceFeatures(false);
  };
  const featureCount = activeLayer?.data?.features.length || 0;
  const layerName = activeLayer?.displayName;
  const onSave = (replaceFeatures: boolean) => {
    const fullLayerData = featureCollection(
      replaceFeatures
        ? pendingFeatures.features
        : [...activeLayer.data.features, ...pendingFeatures.features],
    );
    bulkCreateLayerFeatures({
      farmId,
      layerId: activeLayerId,
      fullLayerData,
      newLayerFeatures: pendingFeatures.features,
      removeExisting: replaceFeatures,
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalBody>
        <h2 className="mb-3">
          Add to or replace existing {layerName} features?
        </h2>
        <FormGroup check className="d-flex align-items-center my-2">
          <Input
            name="layerOption"
            type="radio"
            checked={!shouldReplaceFeatures}
            onChange={() => setShouldReplaceFeatures(false)}
            id="add"
            className="mt-0"
          />
          <Label check htmlFor="add">
            Add to existing features
          </Label>
        </FormGroup>
        <FormGroup check className="d-flex align-items-center my-2">
          <Input
            name="layerOption"
            type="radio"
            checked={shouldReplaceFeatures}
            onChange={() => setShouldReplaceFeatures(true)}
            id="replace"
            className="mt-0"
          />
          <Label check htmlFor="replace">
            Replace{' '}
            {asQuantityWithUnit(
              'existing features',
              'existing feature',
            )(featureCount)}
          </Label>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} size="sm">
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={() => {
            onSave(shouldReplaceFeatures);
            onCompleted();
            toggle();
          }}
          size="sm"
        >
          OK
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ReplaceFeaturesModal;
